import type {
  FulfillmentContextDetail,
  OriginDetail,
  AuditAsset,
  FulfillmentContext,
  Contract,
  Schedule,
  AssetTag,
  CarrierRateSet,
} from '../services/FP_FulfillmentContextService/types';
import type {
  FulfillmentContextDetails,
  FulfillmentContextSummaryRecord,
  ConfiguredCarrierServiceMethod,
  ConfiguredCarrier,
  AuditInfoWithAsset,
  ConfiguredCarrierCount,
} from './types';
import { formatDuration } from 'date-fns/formatDuration';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import type { PartnerAddressV2, PartnerContact, PartnerOrigin, PartnerTenant } from '@shipium-corp/shipium-ts-types';

export const originSummarizer = (origin: OriginDetail) => {
  return {
    originId: origin.origin?.urn,
    name: origin.origin?.name,
    shippingDays: getShippingDays(origin),
    timezone: origin.address?.address?.location?.validatedAddressMetadata?.timeZoneId,
    address: origin.address?.address?.location,
  };
};

export const getShippingDays = (origin: OriginDetail) => {
  const schedules = origin.schedule?.schedule?.entries ?? [];
  const today = new Date();
  const activeSchedule = schedules.find(
    (schedule) => today >= new Date(schedule.from) && today <= new Date(schedule.to),
  );
  if (activeSchedule) {
    return activeSchedule.shippingDays ?? [];
  }
};

export const fulfillmentContextValidator = (context: ContextTableSummary) => {
  const validationMessages: string[] = [];

  if (!context.origin && context.context.fulfillmentType !== 'returns') {
    validationMessages.push('Origin required');
  }

  // Only validate contracts if they're loaded (carriers array exists)
  if (Array.isArray(context.carriers)) {
    if (
      context.carriers.length === 0 &&
      (!Array.isArray(context.context.contractIds) || context.context.contractIds.length === 0) &&
      (!Array.isArray(context.context.injectionProfileIds) || context.context.injectionProfileIds.length === 0)
    ) {
      validationMessages.push('Contract required or Injection Site required');
    }
  }

  return validationMessages;
};

export const humanizeDuration = (start: Date, end: Date) => {
  const duration = intervalToDuration({ end, start });
  if (
    !duration.days &&
    !duration.months &&
    !duration.weeks &&
    !duration.years &&
    !duration.hours &&
    !duration.minutes
  ) {
    return 'Less than a minute';
  }
  if (!duration.days && !duration.months && !duration.weeks && !duration.years) {
    return formatDuration(duration, { format: ['hours', 'minutes'] });
  } else {
    return formatDuration(duration, { format: ['years', 'months', 'days'] });
  }
};

export const fulfillmentContextAudit = (context: FulfillmentContextDetail) => {
  const events: AuditInfoWithAsset[] = [];

  const extendAuditInfo = (assetName: string, asset: AuditAsset): AuditInfoWithAsset[] => {
    const now = new Date();
    const createdDate = new Date(asset.auditInfo.creationTimeStamp);
    const eventDate = new Date(asset.auditInfo.lastUpdatedTimeStamp);
    const eventAge = humanizeDuration(eventDate, now);
    const createdEventAge = humanizeDuration(createdDate, now);
    if (asset.auditInfo.creationTimeStamp === asset.auditInfo.lastUpdatedTimeStamp) {
      return [
        {
          ...asset.auditInfo,
          assetName,
          asset,
          eventDate: createdDate,
          eventType: 'created',
          eventAge: createdEventAge,
        },
      ];
    } else {
      return [
        {
          ...asset.auditInfo,
          assetName,
          asset,
          eventDate: createdDate,
          eventType: 'created',
          eventAge: createdEventAge,
        },
        {
          ...asset.auditInfo,
          assetName,
          asset,
          eventDate,
          eventType: 'modified',
          eventAge,
        },
      ];
    }
  };

  events.push(...extendAuditInfo('Fulfillment Context', context.fulfillmentContext as AuditAsset));

  context.contracts.reduce((prev, curr) => {
    if (curr.carrierAccount && curr.carrierSettings) {
      prev.push(...extendAuditInfo('Carrier Contract', curr.contract as AuditAsset));
      prev.push(...extendAuditInfo('Carrier Account', curr.carrierAccount?.carrierAccount as AuditAsset));
      prev.push(...extendAuditInfo('Carrier Settings', curr.carrierSettings?.carrierSettings as AuditAsset));
    }
    for (const rate of curr.carrierRates || []) {
      prev.push(...extendAuditInfo('Carrier Rates', rate.carrierRate as AuditAsset));
    }

    return prev;
  }, events);

  if (context?.origin?.origin) {
    events.push(...extendAuditInfo('Origins', context.origin.origin as AuditAsset));
  }

  // The schedule carries the origin's processing time and is versioned separately from the
  // origin itself, so an origin edit and a schedule edit produce different auditInfo stamps.
  if (context?.origin?.schedule?.schedule) {
    events.push(...extendAuditInfo('Origin Schedule', context.origin.schedule.schedule as AuditAsset));
  }

  if (context.tenant?.tenant?.auditInfo) {
    events.push(...extendAuditInfo('Tenant', context.tenant?.tenant as AuditAsset));
  }

  context.tags.reduce((prev, curr) => {
    prev.push(...extendAuditInfo('Context Tags', curr as AuditAsset));
    return prev;
  }, events);

  events.sort((a, b) => b.eventDate.valueOf() - a.eventDate.valueOf());

  return events;
};

export const originFilterBuilder = (contexts: FulfillmentContextSummaryRecord[]) => {
  const options = [];
  const originMap = contexts.reduce<Map<string, string>>((prev, curr) => {
    if (!!curr.origin && curr.origin.name && curr.origin.originId) {
      prev.set(curr.origin.originId, curr.origin.name);
    }
    return prev;
  }, new Map<string, string>());

  for (const [key, value] of originMap) {
    options.push({ value: `origin:${key}`, label: value });
  }

  return {
    label: 'Origin',
    options,
  };
};

export const tenantFilterBuilder = (contexts: FulfillmentContextSummaryRecord[]) => {
  const options = [];
  const tenantMap = contexts.reduce<Map<string, string>>((prev, curr) => {
    if (curr.tenantId && curr.tenantName) {
      prev.set(curr.tenantId, curr.tenantName);
    }
    return prev;
  }, new Map<string, string>());

  for (const [key, value] of tenantMap) {
    options.push({ value: `tenant:${key}`, label: value });
  }

  return {
    label: 'Tenant',
    options,
  };
};

export const contextOriginFilter = (context: FulfillmentContextSummaryRecord, originFilters: string[]) => {
  if (originFilters.length === 0) {
    return true;
  }

  if (!context.origin) {
    return false;
  }
  return originFilters.includes(context.origin.originId ?? '');
};

export const contextTenantFilter = (context: FulfillmentContextSummaryRecord, tenantFilters: string[]) => {
  return tenantFilters.length > 0 ? context.tenantId && tenantFilters.includes(context.tenantId) : true;
};

export const filterContexts = (contexts: FulfillmentContextSummaryRecord[], filters?: string[]) => {
  return contexts.filter((context) => {
    if (Array.isArray(filters) && filters.length > 0) {
      const originFilters = filters.filter((filter) => filter.startsWith('origin:')).map((filter) => filter.substr(7));
      const tenantFilters = filters.filter((filter) => filter.startsWith('tenant:')).map((filter) => filter.substr(7));

      return contextOriginFilter(context, originFilters) && contextTenantFilter(context, tenantFilters);
    } else {
      return true;
    }
  });
};

export const carrierServiceMethodSummarizer = (
  contexts: FulfillmentContextDetails,
): ConfiguredCarrierServiceMethod[] => {
  const carrierServiceMethodMap = Object.values(contexts ?? {}).reduce<Record<string, ConfiguredCarrierServiceMethod>>(
    (prev, curr) => {
      for (const contract of curr.contracts) {
        for (const rate of contract.carrierRates || []) {
          const carrierServiceMethodId = rate.carrierRate?.carrierServiceMethodId;
          if (carrierServiceMethodId) {
            if (!prev[carrierServiceMethodId]) {
              prev[carrierServiceMethodId] = {
                carrierServiceMethodId,
                carrierId: rate.carrierRate?.carrier || '',
                contextDetailMap: {
                  [curr.fulfillmentContext.fulfillmentContextId]: curr,
                },
              };
            } else {
              prev[carrierServiceMethodId].contextDetailMap[curr.fulfillmentContext.fulfillmentContextId] = curr;
            }
          }
        }
      }
      return prev;
    },
    {},
  );

  return Object.values(carrierServiceMethodMap ?? {});
};
export const carrierSummarizer = (contexts: FulfillmentContextDetails): ConfiguredCarrier[] => {
  const carrierMap = Object.values(contexts ?? {}).reduce<Record<string, ConfiguredCarrier>>((prev, curr) => {
    for (const contract of curr.contracts) {
      for (const rate of contract.carrierRates || []) {
        const carrierId = rate.carrierRate?.carrier;
        const carrierRate = rate.carrierRate;
        if (carrierId) {
          if (!prev[carrierId]) {
            prev[carrierId] = {
              carrierId,
              carrierServiceMethodMap: {
                [carrierRate?.carrierServiceMethodId || '']: rate.carrierRate!,
              },
            };
          } else {
            prev[carrierId].carrierServiceMethodMap[carrierRate?.carrierServiceMethodId!] = rate.carrierRate!;
          }
        }
      }
    }
    return prev;
  }, {});

  return Object.values(carrierMap ?? {});
};

export const carrierCountSummarizer = (
  contracts: Contract[],
  rateSets: Record<string, CarrierRateSet>,
): ConfiguredCarrierCount[] => {
  const resultMap: Record<string, ConfiguredCarrierCount> = {};

  for (const contract of contracts) {
    const rates = rateSets[contract?.carrierRateSetId]?.carrierRateIds;
    if (rates) {
      if (resultMap[contract.carrierId]) {
        resultMap[contract.carrierId].serviceMethods.push(...rates);
        resultMap[contract.carrierId].methodCount += rates.length;
      } else {
        resultMap[contract.carrierId] = {
          carrierId: contract.carrierId ?? 'Unknown',
          methodCount: rates.length,
          serviceMethods: [...rates],
        };
      }
    }
  }

  return Object.values(resultMap ?? {});
};

export const fulfillmentContextSummarizer = (contexts: ContextTableSummary[]): FulfillmentContextSummaryRecord[] => {
  const all = contexts.map((context) => {
    const validationMessages = fulfillmentContextValidator(context);
    return {
      archived: context.context.archived,
      contextId: context.context.fulfillmentContextId,
      contextName: context.context.name,
      contractIds: context.context.contractIds,
      tags: context?.tags?.map((tag) => tag.name) ?? [],
      origin: context.origin,
      address: context.address,
      schedule: context.schedule,
      carriers: context.carriers,
      isValid: validationMessages.length === 0,
      validationMessages,
      tenantId: context?.tenant?.tenantId,
      tenantName: context?.tenant?.name,
    };
  });

  // Return all contexts (archived and non-archived)
  return all;
};

export interface ContextTableSummary {
  context: FulfillmentContext;
  address?: PartnerAddressV2;
  contact?: PartnerContact;
  returnToContact?: PartnerContact;
  origin: PartnerOrigin | null;
  schedule: Schedule | null;
  tenant?: PartnerTenant | null;
  tags?: AssetTag[];
  carriers: ConfiguredCarrierCount[];
}
export interface BuildContextTableSummariesArgs {
  contexts: FulfillmentContext[];
  contacts: PartnerContact[];
  addresses: PartnerAddressV2[];
  origins: PartnerOrigin[];
  schedules: Schedule[];
  tenants: PartnerTenant[];
  tags: AssetTag[];
  contracts?: Contract[];
  rateSets?: CarrierRateSet[];
}

// Hydrate context summaries client-side instead of calling the hydrated context detail
// endpoint: keeps unneeded data out of memory and collapses many calls into a few.
export const buildContextTableSummaries = ({
  contexts,
  contacts,
  addresses,
  origins,
  schedules,
  tenants,
  tags,
  contracts,
  rateSets,
}: BuildContextTableSummariesArgs) => {
  const contactMap: Record<string, PartnerContact> = {};
  for (const curr of contacts) {
    contactMap[curr.contactId!] = curr;
  }
  const contractMap: Record<string, Contract> = {};
  for (const curr of contracts ?? []) {
    contractMap[curr.contractId] = curr;
  }
  const originMap: Record<string, PartnerOrigin> = {};
  for (const curr of origins) {
    originMap[curr.originId] = curr;
  }
  const addressMap: Record<string, PartnerAddressV2> = {};
  for (const curr of addresses) {
    addressMap[curr.addressId!] = curr;
  }
  const scheduleMap: Record<string, Schedule> = {};
  for (const curr of schedules) {
    scheduleMap[curr.scheduleId] = curr;
  }
  const tenantMap: Record<string, PartnerTenant> = {};
  for (const curr of tenants) {
    for (const context of curr?.links?.fulfillmentContexts ?? []) {
      tenantMap[context.shipiumId] = curr;
    }
  }
  const tagMap: Record<string, AssetTag> = {};
  for (const curr of tags) {
    tagMap[curr.tagId] = curr;
  }
  const rateSetMap: Record<string, CarrierRateSet> = {};
  for (const curr of rateSets ?? []) {
    rateSetMap[curr.carrierRateSetId] = curr;
  }
  return contexts.map((context) => {
    const origin = context.originId ? originMap[context.originId] : null;
    const address = origin?.addressId ? addressMap[origin.addressId] : undefined;
    const contextContracts = context.contractIds?.map((c) => contractMap[c]).filter(Boolean) || [];
    const contact = origin?.contactId ? contactMap[origin.contactId] : undefined;
    const returnToContact = origin?.returnToContactId ? contactMap[origin.returnToContactId] : undefined;
    const schedule = origin?.scheduleId ? scheduleMap[origin?.scheduleId] : null;
    const tagList = context.tagIds?.map((t) => tagMap[t]);

    const carrierCounts: Record<string, ConfiguredCarrierCount> = {};
    for (const contract of contextContracts) {
      const rates = rateSetMap[contract?.carrierRateSetId]?.carrierRateIds;
      if (rates) {
        if (carrierCounts[contract.carrierId]) {
          carrierCounts[contract.carrierId].serviceMethods.push(...rates);
          carrierCounts[contract.carrierId].methodCount += rates.length;
        } else {
          carrierCounts[contract.carrierId] = {
            carrierId: contract.carrierId ?? 'Unknown',
            methodCount: rates.length,
            serviceMethods: [...rates],
          };
        }
      }
    }

    return {
      schedule,
      context,
      tags: tagList,
      address,
      origin,
      contact,
      returnToContact,
      carriers: Object.values(carrierCounts ?? {}),
      tenant: context.fulfillmentContextId ? tenantMap[context.fulfillmentContextId] : null,
    };
  });
};
